import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { BehaviorSubject, timer, Subscription, interval, of } from 'rxjs';
import { catchError, switchMap, takeWhile } from 'rxjs/operators';
import { ApiService } from './api.service';
import { CandidateModel } from '../models/candidate.model';
import { AcModel } from '../models/ac.model';
import { AuthStateService } from './auth-state.service';
import { UiService } from './ui.service';
import { TranslocoService } from '@ngneat/transloco';
import { TimezoneService } from './timezone.service';
import { AcStatusSettingsExerciseGroupType, AcStatusTimingsType, AcStatusUserType, AcTimingStatus, BusyType } from '../models/acstatus/ac-status.type';
import { SlotModel } from '../models/slot.model';
import { AuthUserModel } from 'projects/core/src/lib/models/authuser.model';
import { TokenService } from './token.service';
import { GroupModel } from '../models/group.model';
import { TIMING_DIV } from '../models/constants';
import { ObserverModel } from '../models/observer.model';
import { ExerciseModel } from '../models/exercise.model';

const POLLING_INTERVAL_STANDARD = 5;

export enum ExerciseSettingsMode {
  Setting = 'settings',
  Start = 'start'
}

export interface ExerciseSettingsModeType {
  exercise?: number;
  slot?: number;
  mode: ExerciseSettingsMode
}

@Injectable({
  providedIn: 'root'
})
export class DataService {

  private userStatus: AcStatusUserType = {
    nav: null,
    slot: null,
    element: null,
    avatar: null
  };
  private userStatusSubject: BehaviorSubject<AcStatusUserType> = new BehaviorSubject(this.userStatus);
  public userStatus$ = this.userStatusSubject.asObservable();

  public ac: AcModel = new AcModel();
  private acSubject: BehaviorSubject<AcModel> = new BehaviorSubject(null);
  public ac$ = this.acSubject.asObservable();

  private exerciseSettings: BehaviorSubject<ExerciseSettingsModeType> = new BehaviorSubject(null);
  public exerciseSettings$ = this.exerciseSettings.asObservable();

  private sendingTiming: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public sendingTiming$ = this.sendingTiming.asObservable();

  private networkError: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public networkError$ = this.networkError.asObservable();

  private polling: Subscription = null;
  private pollingActive = true;

  // temp
  private timezones: BehaviorSubject<Array<any>> = new BehaviorSubject([]);
  public timezones$ = this.timezones.asObservable();
  // temp

  constructor(
    private api: ApiService,
    private auth: AuthStateService,
    private token: TokenService,
    private ui: UiService,
    private router: Router,
    private timezone: TimezoneService,
    private transloco: TranslocoService
  ) {
    this.initPolling();
  }

  public setAc(ac: AcModel): void {
    this.ac = ac;
    this.acSubject.next(this.ac);
  }

  // personal exercise and element control

  public setActiveNav(nav: string): void {
    this.userStatus.nav = nav;
    this.userStatus.slot = null;
    this.userStatus.element = null;
    this.userStatusSubject.next(this.userStatus);
  }

  public setActiveExercise(id: number): void {
    this.userStatus.nav = 'exercise';
    this.userStatus.slot = id;
    this.userStatusSubject.next(this.userStatus);
  }

  public setActiveElement(id: number): void {
    this.userStatus.nav = 'exercise';
    this.userStatus.element = id;
    this.userStatusSubject.next(this.userStatus);
  }

  public setAvatar(url: string | null): void {
    this.userStatus.avatar = url;
    this.userStatusSubject.next(this.userStatus);
  }

  // manage exercise settings

  public openExerciseSettings(exercise: number, mode: ExerciseSettingsMode = ExerciseSettingsMode.Start): void {
    if (this.auth.isModerator()) {
      this.exerciseSettings.next({
        exercise,
        mode
      });
    }
  }

  public closeExerciseSettings(): void {
    this.exerciseSettings.next(null);
  }

  // polling

  public setPolling(pollingActive: boolean): void {
    this.pollingActive = pollingActive;
  }

  private initPolling(): void {
    if (this.polling === null) {
      this.polling = interval(POLLING_INTERVAL_STANDARD * 1000)
        .pipe(
          switchMap(() => {
            if (this.pollingActive && this.ac.loaded && !this.ac.isFinished()) {
              return this.api.getStatus(this.userStatus).pipe(
                catchError(() => {
                  this.networkError.next(true);
                  return of(null);
                })
              )
            } else {
              return of(null);
            }
          })
        )
        .subscribe({
          next: (result: any) => {
            if (result !== null) {
              this.networkError.next(false);
              this.setNewAcStatus(result.data);
            }
          }
        });
    }
  }

  // status and timing

  public setNewAcStatus(data: any): void {
    const oldMainStatus = this.ac.status;
    this.ac.ac_status = data;
    // timezones
    this.timezone.setTimezonesFromHashs(this.ac.ac_status.users);
    // events
    const hash = this.auth.getHashedId();
    const me = this.ac.getUser(hash);
    const oldBusy: BusyType = me?.busy || null;
    const hasChangedData = this.ac.mapAcStatus();
    if (hasChangedData === true) {
      this.acSubject.next(this.ac);
    }
    const newBusy: BusyType = me?.busy || null;
    this.reactOnStatusEvents(oldBusy, newBusy, me);
    // show toasts for main status
    if (this.ac.status !== oldMainStatus) {
      if (this.ac.status === AcTimingStatus.Running) {
        this.ui.toastSuccess(this.transloco.translate('dashboard.messages.ac_has_been_started'), this.transloco.translate('dashboard.messages.started'));
      } else if (this.ac.status === AcTimingStatus.Finished) {
        this.ui.toastSuccess(this.transloco.translate('dashboard.messages.ac_has_been_finished'), this.transloco.translate('dashboard.messages.finish'));
      } else if (this.ac.status === AcTimingStatus.Cancelled) {
        this.ui.toastSuccess(this.transloco.translate('dashboard.messages.ac_has_been_terminated'), this.transloco.translate('dashboard.messages.finish'));
      }
    }
    // show toast for participant changes
    if (this.ac.hasParticipantsChanged(data.amounts?.c, data.amounts?.o)) {
      this.ac.loaded = false;
      this.ui.showParticipantsChanged(this.ac);
    }
  }

  private reactOnStatusEvents(oldBusy: BusyType, newBusy: BusyType, me: CandidateModel | ObserverModel): void {
    const oldSlot = oldBusy?.slot || null;
    const newSlot = newBusy?.slot || null;
    let wasStarted = false;
    let wasFinished = false;
    let slot = null;
    let candidate = null;
    let group = null;
    let mode = null;
    if (
      (oldSlot === null && newSlot !== null) ||
      (newSlot !== null && oldSlot !== null && newSlot !== oldSlot) ||
      (newSlot !== null && oldSlot !== null && newSlot === oldSlot && oldBusy?.mode !== newBusy?.mode)
    )
    {
      // something started
      wasStarted = true;
      slot = newSlot;
      group = newBusy?.group || null;
      candidate = newBusy?.candidate || null;
      mode = newBusy?.mode || null;
    } else if (oldSlot !== null && newSlot === null) {
      // something finished
      wasFinished = true;
      slot = oldSlot;
      group = oldBusy?.group || null;
      candidate = oldBusy?.candidate || null;
      mode = oldBusy?.mode || null;
    }
    if (this.auth.isCandidate()) {
      candidate = me;
    }
    const url = this.router.url;
    let event: string = null;
    let target: string = wasFinished ? '/ac/dashboard' : null;

    if (slot?.type === 'conference' && this.auth.isObserver()) {
      event = wasFinished ? 'conference_finished' : 'conference_started';
      if (wasStarted) {
        target = '/ac/conference';
      }
    } else if (slot !== null && group !== null) {
      if (this.auth.isObserver() && !group.isGroupModerator(me.hashed_id)) {
        if (slot.type === 'exercisepart') {
          event = wasFinished ? mode + '_finished' : mode + '_started';
          if (wasStarted) {
            target = '/ac/' + mode;
            if (slot) {
              target += '/' + slot.id;
            }
            if (group) {
              target += '/' + group.hash;
            }
            if (candidate) {
              target += '/' + candidate.hashed_id;
            }
          }
        }
      } else if (this.auth.isCandidate() && slot.type === 'exercisepart' && mode === 'exercise') {
        event = wasFinished ? 'exercise_finished' : 'exercise_started';
        if (wasStarted) {
          target = '/ac/exercise';
          if (slot) {
            target += '/' + slot.id;
          }
          if (group) {
            target += '/' + group.hash;
          }
          if (me) {
            target += '/' + me.hashed_id;
          }
        }
      }
    }

    if (event !== null && target !== null && target !== url) {
      this.ui.showRoutingInfo(
        this.transloco.translate('services.data.events.' + event + '.topline'),
        this.transloco.translate('services.data.events.' + event + '.message'),
        [target]
      );
    }
  }

  // timing

  /**
   * Sends changed timing settings to the server
   *
   * @param slot SlotModel
   * @param hash string
   * @param timing AcStatusTimingsType
   */
  public setTiming(slot: SlotModel, hash: string, timing: AcStatusTimingsType): void {
    this.sendingTiming.next(true);
    this.api.setStatusTiming(slot.id.toString() + TIMING_DIV + hash, timing).subscribe({
      next: (result: any) => {
        this.setNewAcStatus(result.data);
        this.sendingTiming.next(false);
      }
    });
  }

  /**
   * Gets the hash value for timing (candidate or group)
   *
   * @param slot SlotModel
   * @param candidate CandidateModel
   * @param group GroupModel
   * @returns string | null
   */
  public getHash(slot: SlotModel, candidate: CandidateModel = null, group: GroupModel = null): string | null {
    if (slot) {
      if (slot.type === 'conference') {
        return 'conference';
      } else if (this.ac.isSingle()) {
        return this.ac.candidates[0].hashed_id;
      } else if (slot.exercise?.isIndividual() && candidate !== null) {
        return candidate.hashed_id;
      } else if (slot.exercise?.isTeam() && group !== null) {
        return group.hash;
      }
    }
    return null;
  }

  /**
   * Gets the hash id for the group of a candidate in a specific exercise
   *
   * @param exerciseId number
   * @param candidateHash string
   * @returns string | null
   */
  public getGroupsForExercise(exerciseId: number): AcStatusSettingsExerciseGroupType | null {
    const status = this.ac?.ac_status;
    if (
      status.settings?.exercises[exerciseId]
    ) {
      return status.settings?.exercises[exerciseId].groups || null;
    }
    return null;
  }

  /**
   * Gets the hash id for the group of a candidate in a specific exercise
   *
   * @param exerciseId number
   * @param candidateHash string
   * @returns string | null
   */
  public getGroupForCandidate(exerciseId: number, candidateHash: string): string | null {
    const status = this.ac?.ac_status;
    if (
      status.settings?.exercises[exerciseId] &&
      status.settings?.exercises[exerciseId].candidates[candidateHash] &&
      status.settings?.exercises[exerciseId].candidates[candidateHash].group
    ) {
      return status.settings?.exercises[exerciseId].candidates[candidateHash].group || null;
    }
    return null;
  }

  /**
   * Gets the name of a group in an exercise
   *
   * @param exerciseId number
   * @param groupHash string
   * @returns string | null
   */
  public getGroupName(exerciseId: number, groupHash: string): string | null {
    const status = this.ac?.ac_status;
    if (
      status.settings?.exercises[exerciseId] &&
      status.settings?.exercises[exerciseId].groups[groupHash]
    ) {
      return status.settings?.exercises[exerciseId].groups[groupHash].name || null;
    }
    return null;

  }

  /**
   * Checks if I (auth) or a specific user is moderator for this exercise
   *
   * @param exerciseId number
   * @param groupId string
   * @param user AuthUserModel
   * @returns boolean
   */
  public isExerciseModerator(exerciseId: number, groupId: string, user: AuthUserModel = null): boolean {
    if (user === null) {
      user = this.token.getAuthUser();
    }
    const status = this.ac?.ac_status;
    if (this.token.isModerator()) {
      return true;
    } else if (
      status.settings?.exercises[exerciseId] &&
      status.settings?.exercises[exerciseId].groups[groupId] &&
      status.settings?.exercises[exerciseId].groups[groupId].moderator === user.hashed_id
    ) {
      return true;
    }
    return false;
  }

  /**
   * Checks if I (auth) can rate a candidate
   */
  public canIRateCandidate(exercise: ExerciseModel, candidate: CandidateModel, group: GroupModel, user: ObserverModel | CandidateModel): boolean {
    if (group === null) {
      // conference / onlinetest
      return true;
    } else if (this.ac.isSingle()) {
      // single ac
      return true;
    } else if (group.isInGroup(candidate.hashed_id)) {
      if (exercise.isIndividual()) {
        return true;
      }
      return group.isLinkedToCandidate(candidate.hashed_id, user?.hashed_id) || this.auth.isModerator();
    }
    return false;
  }


  // temp
  public setTimezones(timezones: Array<any>): void {
    this.timezones.next(timezones);
  }
  // temp

}
